/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ParentSize } from "@visx/responsive";
import { When } from "react-if";
import { FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { ReservationUtilizationTimePeriod } from "~/generated/graphql";
import {
  DataContainer,
  DataItem,
  MonthlyUtilisationChart,
  monthlyUtilisationChartLegend,
} from "~/components";
import { Legend } from "~/components/Charts/components";
import { useCurrency } from "~/hooks";
import { formatMoney, isNotEmpty, isNotNil } from "~/tools";
import { useGetUtilizationData } from "../hooks";

type Props = {
  period: ReservationUtilizationTimePeriod;
  nordcloudId: string;
};

export function UtilizationChart({ nordcloudId, period }: Props) {
  const { currency } = useCurrency();
  const { loading, chartData, benefit, forecast, utilizationValue } =
    useGetUtilizationData({ nordcloudId, period });

  return (
    <>
      <FlexContainer justifyContent="center">
        <ParentSize>
          {(parent) => (
            <MonthlyUtilisationChart
              width={parent.width}
              height={280}
              data={chartData.filter(isNotNil)}
              loading={loading}
              period={period}
            />
          )}
        </ParentSize>
      </FlexContainer>
      <Spacer height={theme.spacing.spacing03} />
      <FlexContainer justifyContent="flex-end">
        <Legend inline data={monthlyUtilisationChartLegend} />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing04} />
      <When condition={isNotEmpty(chartData)}>
        <DataContainer>
          <DataItem label="Total Utilisation" value={`${utilizationValue}%`} />
          {forecast && (
            <DataItem label="Total Forecast" value={`${forecast}%`} />
          )}
          <DataItem
            label="Total Benefits"
            value={formatMoney(benefit, currency)}
          />
        </DataContainer>
      </When>
    </>
  );
}
